import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { goToCheckout } from '../utils/tracking';
interface BonusCardProps {
  tag: string;
  icon: string;
  name: string;
  description: string;
  extendedDescription: string;
  originalValue: string;
  colSpan?: boolean;
}
function BonusCard({
  tag,
  icon,
  name,
  description,
  extendedDescription,
  originalValue,
  colSpan
}: BonusCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  return <div className={`relative rounded-2xl border border-neon/10 bg-card p-6 pt-7 transition-all duration-300 hover:-translate-y-1 hover:border-neon/30 hover:shadow-[0_10px_40px_rgba(0,0,0,0.3)] ${colSpan ? 'md:col-span-2' : ''}`} data-id="element-44">
      <div className="absolute -top-3 left-5 rounded-full bg-neon px-3.5 py-1 text-[10px] font-black tracking-[1px] text-black" data-id="element-45">
        {tag}
      </div>
      <div className="mb-3 text-[34px]" data-id="element-46">{icon}</div>
      <h3 className="mb-2 text-[16px] font-bold text-offwhite" data-id="element-47">{name}</h3>
      <p className="mb-3.5 text-[13px] leading-[1.6] text-coolgray" data-id="element-48">
        {description}
      </p>

      {isExpanded && <div className="mb-4 mt-2 rounded-lg bg-slate/50 p-4 text-[13px] leading-[1.6] text-coolgray border border-white/5" data-id="element-49">
          {extendedDescription}
        </div>}

      <button onClick={() => setIsExpanded(!isExpanded)} className="mb-4 flex items-center gap-1 text-[12px] font-bold text-neon hover:text-neon-dark transition-colors" data-id="element-50">
        {isExpanded ? <>
            Read Less <ChevronUp className="h-3 w-3" data-id="element-51" />
          </> : <>
            Read More <ChevronDown className="h-3 w-3" data-id="element-52" />
          </>}
      </button>

      <div className="flex items-center gap-2.5" data-id="element-53">
        <span className="font-mono text-[13px] text-danger line-through" data-id="element-54">
          {originalValue}
        </span>
        <span className="rounded bg-neon/10 px-2.5 py-0.5 font-mono text-[13px] font-bold text-neon" data-id="element-55">
          FREE TODAY
        </span>
      </div>
    </div>;
}
export function BundleSection() {
  return <section id="order" className="bg-slate px-5 py-20" data-id="element-56">
      <div className="mx-auto max-w-[900px]" data-id="element-57">
        <p className="mb-4 text-center font-mono text-[11px] uppercase tracking-[3px] text-neon" data-id="element-58">
          // EVERYTHING YOU GET TODAY
        </p>
        <h2 className="mb-4 text-center text-[clamp(26px,4vw,42px)] font-black leading-[1.2] text-offwhite" data-id="element-59">
          The Complete <span className="text-neon" data-id="element-60">Unstoppable Brain</span>{' '}
          Bundle
        </h2>
        <p className="mx-auto mb-[50px] max-w-[650px] text-center text-[17px] leading-[1.7] text-coolgray" data-id="element-61">
          6 science-backed guides. One 15-minute protocol. A completely rewired
          biology.
        </p>

        <div className="relative mb-6 overflow-hidden rounded-[20px] border-2 border-neon/30 bg-gradient-to-br from-card to-[#0D1F3C] p-10" data-id="element-62">
          <div className="absolute right-5 top-5 rounded-full bg-neon px-3 py-1 text-[10px] font-black tracking-[1px] text-black" data-id="element-63">
            ⭐ MAIN PRODUCT
          </div>

          <div className="mb-5 flex flex-col gap-5 md:flex-row md:items-start md:justify-between" data-id="element-64">
            <div data-id="element-65">
              <h3 className="mb-2 text-[22px] font-black text-offwhite" data-id="element-66">
                🧠 THE UNSTOPPABLE BRAIN — Main System
              </h3>
              <p className="text-[14px] leading-[1.6] text-coolgray" data-id="element-67">
                The flagship 60-page minimalist biohacking manual built on the
                latest neurobiology research from Stanford and Harvard. Chapter
                by chapter, you will master the science of focus, energy, and
                deep sleep — without supplements, machines, or hours of your
                time.
              </p>
            </div>
            <div className="shrink-0 text-left md:text-right" data-id="element-68">
              <div className="font-mono text-[14px] text-danger line-through" data-id="element-69">
                $97.00
              </div>
              <div className="font-mono text-[24px] font-black text-neon" data-id="element-70">
                INCLUDED
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-2 md:grid-cols-2" data-id="element-71">
            {['The Circadian Architecture Blueprint', 'The 15-Minute Morning Protocol', 'Adenosine & Caffeine Mastery', 'The Deep Work Ultradian Cycle System', 'Nutritional Cognitive Edges', 'The 30-Day Action Plan'].map((feature, i) => <div key={i} className="flex items-center gap-2 text-[13px] text-coolgray" data-id="element-72">
                <span className="font-bold text-neon" data-id="element-73">✓</span> {feature}
              </div>)}
          </div>
        </div>

        <div className="mb-10 grid grid-cols-1 gap-5 md:grid-cols-2" data-id="element-74">
          <BonusCard tag="BONUS 1 — FREE" icon="☀️" name="The 5-Minute Morning Ignition" description="Your daily step-by-step biological 'start button.' A laminated-style cheat sheet with the exact morning sequence to prime your brain for all-day focus." extendedDescription="Includes the exact timing for light exposure, hydration protocols, and the 2-minute movement sequence that spikes cortisol naturally. Print it and tape it to your bathroom mirror for foolproof execution every single morning." originalValue="$27.00" data-id="element-75" />
          <BonusCard tag="BONUS 2 — FREE" icon="🌙" name="The Sleep Matrix" description="How to engineer the ultimate recovery sanctuary. The 65°F rule, Digital Sunset Protocol, Caffeine Half-Life Table, and the exact 3-supplement stack for deep sleep." extendedDescription="Discover why 8 hours of bad sleep leaves you exhausted, while 6 hours of optimized sleep feels like a superpower. Includes the exact temperature curve protocol, light-blocking strategies, and the only 3 supplements scientifically proven to increase REM sleep without grogginess." originalValue="$47.00" data-id="element-76" />
          <BonusCard tag="BONUS 3 — FREE" icon="⚡" name="The 10-Min Recovery Matrix" description="Burnout-prevention protocols for high-stress days. NSDR (Non-Sleep Deep Rest), the Physiological Sigh technique, and 'White Space' methods." extendedDescription="When you hit a wall, don't reach for coffee. Use these 3 scientifically-validated techniques to reset your autonomic nervous system in under 10 minutes. Perfect for between intense meetings or when transitioning from work to family time." originalValue="$47.00" data-id="element-77" />
          <BonusCard tag="BONUS 4 — FREE" icon="🧪" name="The Supplement Tier List 2026" description="Stop wasting money. A brutally honest Tier S/A/F breakdown of every major supplement. What actually crosses the blood-brain barrier, what is pure marketing." extendedDescription="We analyzed over 50 popular 'nootropics' and biohacking supplements. Discover the 4 compounds that actually work, the 12 that are complete scams, and exactly which brands are verified for purity in the USA. Save hundreds of dollars on your next supplement order." originalValue="$27.00" data-id="element-78" />
          <BonusCard tag="BONUS 5 — FREE" icon="📵" name="The Digital Detox Blueprint" description="Reclaim your dopamine and deep focus from your smartphone. Greyscale Mode setup, the Notification Audit, and Deep Work Batching System." extendedDescription="Your phone is engineered to hijack your attention. Learn the 5-minute settings tweak that makes your phone 80% less addictive, how to structure 'dopamine fasts' during work hours, and the scientifically-backed Phone-Bedroom Ban protocol that transforms your mornings." originalValue="$17.00" colSpan={true} data-id="element-79" />
        </div>

        {/* Value Box */}
        <div className="rounded-[20px] border-2 border-neon/25 bg-gradient-to-br from-neon/10 to-neon/5 px-5 py-[50px] text-center md:px-10" data-id="element-80">
          <p className="mb-4 font-mono text-[11px] uppercase tracking-[3px] text-neon" data-id="element-81">
            // TODAY'S DEAL BREAKDOWN
          </p>

          {/* Bundle Mockup Image */}
          <div className="mx-auto mb-8 max-w-[600px] overflow-hidden rounded-2xl" data-id="element-82">
            <img src="https://cdn.magicpatterns.com/uploads/jNHhzG31udKgk2gLn2JBkN/ChatGPT_Image_9_mar_2026,_14_35_52.png" alt="The Unstoppable Brain — Complete Bundle Mockup" className="w-full h-auto object-cover" data-id="element-83" />
          </div>

          <div className="mx-auto my-[30px] flex max-w-[420px] flex-col overflow-hidden rounded-xl bg-card text-left" data-id="element-84">
            <div className="flex justify-between border-b border-white/5 px-5 py-3.5 text-[14px] text-coolgray" data-id="element-85">
              <span data-id="element-86">The Unstoppable Brain (Main)</span>
              <span className="font-mono text-danger line-through" data-id="element-87">$97.00</span>
            </div>
            <div className="flex justify-between border-b border-white/5 px-5 py-3.5 text-[14px] text-coolgray" data-id="element-88">
              <span data-id="element-89">Morning Ignition Cheat Sheet</span>
              <span className="font-mono text-danger line-through" data-id="element-90">$27.00</span>
            </div>
            <div className="flex justify-between border-b border-white/5 px-5 py-3.5 text-[14px] text-coolgray" data-id="element-91">
              <span data-id="element-92">The Sleep Matrix</span>
              <span className="font-mono text-danger line-through" data-id="element-93">$47.00</span>
            </div>
            <div className="flex justify-between border-b border-white/5 px-5 py-3.5 text-[14px] text-coolgray" data-id="element-94">
              <span data-id="element-95">The Recovery Matrix</span>
              <span className="font-mono text-danger line-through" data-id="element-96">$47.00</span>
            </div>
            <div className="flex justify-between border-b border-white/5 px-5 py-3.5 text-[14px] text-coolgray" data-id="element-97">
              <span data-id="element-98">Supplement Tier List 2026</span>
              <span className="font-mono text-danger line-through" data-id="element-99">$27.00</span>
            </div>
            <div className="flex justify-between border-b border-white/5 px-5 py-3.5 text-[14px] text-coolgray" data-id="element-100">
              <span data-id="element-101">Digital Detox Blueprint</span>
              <span className="font-mono text-danger line-through" data-id="element-102">$17.00</span>
            </div>
            <div className="flex justify-between bg-neon/5 px-5 py-3.5 text-[18px] font-black text-offwhite" data-id="element-103">
              <span data-id="element-104">TOTAL VALUE</span>
              <span className="font-mono text-danger line-through" data-id="element-105">
                $310.00
              </span>
            </div>
          </div>

          <div className="my-[30px]" data-id="element-106">
            <p className="mb-2 font-mono text-[14px] text-coolgray" data-id="element-107">
              // YOUR PRICE TODAY
            </p>
            <div className="font-mono text-[clamp(64px,12vw,100px)] font-black leading-none tracking-[-3px] text-neon" data-id="element-108">
              $6.97
            </div>
            <p className="mt-2 text-[13px] text-coolgray" data-id="element-109">
              One-time payment. No subscriptions. No hidden fees. Ever.
            </p>
          </div>

          <button onClick={e => {
          e.preventDefault();
          goToCheckout();
        }} className="mx-auto mb-4 block w-full max-w-[520px] cursor-pointer rounded-lg bg-gradient-to-br from-neon to-neon-dark px-9 py-5 text-center text-[17px] font-black text-black no-underline transition-all duration-300 hover:-translate-y-0.5 animate-pulse-glow border-none" data-id="element-110">
            ⚡ YES! GIVE ME THE COMPLETE BUNDLE FOR $6.97
          </button>

          <div className="mt-3 flex flex-wrap items-center justify-center gap-4 text-[12px] text-coolgray" data-id="element-111">
            <span data-id="element-112">🔒 Secure Checkout</span>
            <span data-id="element-113">⚡ Instant Access</span>
            <span data-id="element-114">✅ 7-Day Money-Back Guarantee</span>
          </div>
        </div>
      </div>
    </section>;
}